import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import config from "./config";

const endpoint = `${config.API_URL}api/User/`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

export async function getPersonalServices(signal) {
  const { data } = await axios.get(`${endpoint}GetMyServicesAndSelections`, { headers: headers(), signal, timeout: 15000 });
  // Preserve the browser view model; the shared API uses camel case.
  return data.services.map(service => ({
    ServiceId: service.serviceId, ServiceName: service.serviceName,
    ServiceDisplayName: service.serviceDisplayName || service.serviceName,
    ServiceCurrentStatus: service.serviceCurrentStatus, IsSelected: service.isSelected,
  }));
}

export function usePersonalServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [lastSynced, setLastSynced] = useState(null);
  const mounted = useRef(false);
  const revision = useRef(0);
  const reading = useRef(null);
  const writing = useRef(false);

  const cancelRead = useCallback(() => {
    revision.current += 1;
    reading.current?.abort();
    reading.current = null;
  }, []);

  const refresh = useCallback(async () => {
    if (reading.current || writing.current || !mounted.current) return;
    const controller = new AbortController();
    reading.current = controller;
    const version = ++revision.current;
    const token = localStorage.getItem("token");
    setRefreshing(true);
    try {
      const result = await getPersonalServices(controller.signal);
      if (!mounted.current || version !== revision.current || token !== localStorage.getItem("token")) return;
      setServices(result);
      setLastSynced(new Date());
      setError("");
    } catch (err) {
      if (mounted.current && version === revision.current && !controller.signal.aborted) {
        setError("Couldn't sync your services. Your last saved selections are shown. Try refreshing.");
      }
    } finally {
      if (mounted.current && version === revision.current) {
        reading.current = null;
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    refresh();
    const onVisible = () => { if (document.visibilityState !== "hidden") refresh(); };
    const timer = window.setInterval(onVisible, 30000);
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      mounted.current = false;
      cancelRead();
      window.clearInterval(timer);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh, cancelRead]);

  const setSelected = async (serviceId, isSelected) => {
    if (writing.current) return;
    writing.current = true;
    cancelRead();
    setRefreshing(false);
    setSavingId(serviceId);
    setError("");
    const token = localStorage.getItem("token");
    try {
      await axios.post(`${endpoint}UpdateMyServiceSelection`, { serviceId, isSelected }, { headers: headers(), timeout: 15000 });
      if (!mounted.current || token !== localStorage.getItem("token")) return;
      setServices(current => current.map(service => service.ServiceId === serviceId ? { ...service, IsSelected: isSelected } : service));
      setLastSynced(new Date());
    } catch (err) {
      if (mounted.current) setError("Couldn't save this selection. Refresh to check its saved state, then try again.");
    } finally {
      writing.current = false;
      if (mounted.current) setSavingId(null);
    }
  };

  return { services, loading, refreshing, error, savingId, lastSynced, refresh, setSelected };
}
